/**
 * Construye el Snapshot que se envía al navegador a partir de la lectura cruda del libro
 * (UNA llamada values.batchGet). Aplica la restricción de costos (decisión 3 del dueño):
 * los roles sin permiso NO reciben COS-01 ni columnas monetarias ni los KPIs 4 y 12.
 */
import { colToIndex, indexToCol } from '../a1';
import { isBlank } from '../formulas/sheets-semantics';
import {
  DASHBOARD_SHEET,
  FIRST_DATA_ROW,
  FORMAT_SHEETS,
  HEADER_ROW,
  KPI_ROWS,
  LAST_DATA_ROW,
  LISTS_SHEET,
  LIST_NAMES,
  SCHEMA,
  costColumns,
  sheetReadRange,
  type ListName,
  type SheetName,
} from '../schema';
import type { CellValue } from '../workbook';
import { KPI_FORMULAS, normalizeSheetFormula } from './kpi-formulas';
import type { DashboardRow, SheetRows, Snapshot } from './snapshot';

/** Rangos de la lectura única del snapshot (en este orden). */
export const SNAPSHOT_RANGES: string[] = [
  ...FORMAT_SHEETS.map(sheetReadRange),
  `${DASHBOARD_SHEET}!A1:F19`,
  `${LISTS_SHEET}!A1:T60`,
  "'CAL-02'!D3",
  "'INS-01'!D3",
  "'COS-01'!D3",
];

export interface RawRead {
  takenAt: number;
  /** Matrices en el orden de SNAPSHOT_RANGES. */
  values: CellValue[][][];
}

export interface Structure {
  timeZone: string;
  /** Fórmulas de Dashboard!C6:C17 (modo FORMULA). */
  dashboardFormulas: string[];
}

/** Hojas cuyo encabezado (fila 4) no coincide con el SCHEMA: escritura bloqueada. */
export function detectDrift(raw: RawRead): SheetName[] {
  const out: SheetName[] = [];
  FORMAT_SHEETS.forEach((name, i) => {
    const header = raw.values[i]?.[0] ?? [];
    if (SCHEMA[name].columns.some((c, k) => String(header[k] ?? '') !== c.header)) out.push(name);
  });
  return out;
}

export function readLists(listas: CellValue[][]): Partial<Record<ListName, string[]>> {
  const out: Partial<Record<ListName, string[]>> = {};
  LIST_NAMES.forEach((name, c) => {
    if (String(listas[0]?.[c] ?? '') !== name) return;
    const values: string[] = [];
    // Desde la fila 2 hasta la PRIMERA celda vacía (Listas!A30 tiene una nota suelta).
    for (let r = 1; r < listas.length && !isBlank(listas[r]?.[c]); r++) values.push(String(listas[r]![c]));
    out[name] = values;
  });
  return out;
}

export function buildSnapshot(
  raw: RawRead,
  structure: Structure,
  opts: { canViewCosts: boolean; today: number; now?: number; stale?: boolean },
): Snapshot & { drift: SheetName[] } {
  const now = opts.now ?? Date.now();
  const sheets: Partial<Record<SheetName, SheetRows>> = {};
  FORMAT_SHEETS.forEach((name, i) => {
    const def = SCHEMA[name];
    if (def.costSheet && !opts.canViewCosts) return;
    const restricted = opts.canViewCosts ? [] : costColumns(name).map((c) => colToIndex(c.col));
    const matrix = raw.values[i] ?? [];
    const rows: SheetRows['rows'] = [];
    for (let r = FIRST_DATA_ROW; r <= LAST_DATA_ROW; r++) {
      const line = matrix[r - HEADER_ROW] ?? [];
      if (!line.some((v) => !isBlank(v))) continue;
      const v = def.columns.map((_, k) => (restricted.includes(k) ? null : (line[k] ?? '')));
      rows.push({ r, v });
    }
    sheets[name] = { restrictedCols: restricted.map(indexToCol), rows };
  });

  const dash = raw.values[FORMAT_SHEETS.length] ?? [];
  const dashboard: DashboardRow[] = KPI_ROWS.map(({ row, id, cost }, k) => {
    const line = dash[row - 1] ?? [];
    const hidden = cost && !opts.canViewCosts;
    return {
      row,
      id,
      indicator: String(line[0] ?? ''),
      how: String(line[1] ?? ''),
      sheetValue: hidden ? null : (line[2] ?? ''),
      direction: String(line[3] ?? ''),
      goal: hidden ? null : (line[4] ?? ''),
      sheetStatus: hidden ? '' : String(line[5] ?? ''),
      formulaOk: normalizeSheetFormula(structure.dashboardFormulas[k]) === normalizeSheetFormula(KPI_FORMULAS[id]),
      restricted: hidden,
    };
  });

  const p = (i: number) => raw.values[FORMAT_SHEETS.length + 2 + i]?.[0]?.[0] ?? '';
  return {
    generatedAt: raw.takenAt,
    ageMs: Math.max(0, now - raw.takenAt),
    stale: opts.stale ?? false,
    timeZone: structure.timeZone,
    today: opts.today,
    lists: readLists(raw.values[FORMAT_SHEETS.length + 1] ?? []),
    params: { torqueTolerance: p(0), warningDays: p(1), laborCostPerHour: opts.canViewCosts ? p(2) : null },
    dashboard,
    sheets,
    canViewCosts: opts.canViewCosts,
    drift: detectDrift(raw),
  };
}
